import React from "react";
import "./ToxicologicalServicesAndAssessment.css";
import PageHeader from "../../components/headers/PageHeader";
import wave from "../../assets/Images/waves/mix_wave.jpg";

export default function ToxicologicalServicesAndAssessment() {
    return (
        <div>
            <PageHeader
                mediaSrc={wave}
                mediaType={"image"}
                subtitle={"Enhance Clinical Insight"}
                title={"Toxicological Services And Assessment"}
            />

            {/* Intro */}
            <section className="tox-section tox-introSection">
                <h2>
                    We deliver regulatory data solutions faster, accurate, hassle-free &
                    in a cost-effective way
                </h2>
                <p>
                    Accelerate your path to regulatory acceptance with Eupraxia’s
                    end-to-end SEND and nonclinical data services. From raw study data to
                    validated, submission-ready SEND packages — with precision,
                    compliance, and collaboration built in.
                </p>
            </section>

            {/* ====== SECTION 1: Quantitative Risk Assessment & PDE ====== */}
            <section className="tox-section tox-cardSection">
                <h2>Quantitative Risk Assessment &amp; PDE</h2>

                <div className="tox-cardRow">
                    {/* LEFT CARD — li with nested ul */}
                    <article className="tox-card">
                        <ul className="tox-cardList">
                            <li>
                                Risk Assessment
                                <ul>
                                    <li>
                                        PDE (Permitted Daily Exposure) / ADE (Accepted Daily
                                        Exposure)
                                    </li>
                                    <li>OEL (Occupational Exposure Limit) and OEL Banding</li>
                                    <li>AI (Acceptable Intake)</li>
                                </ul>
                            </li>

                            <li>
                                Comprehensive PDE report including:
                                <ul>
                                    <li>Hazard alert indications / summary</li>
                                    <li>
                                        Review of acute toxicity (LD50), skin/eye irritation, and
                                        sensitisation potential
                                    </li>
                                    <li>Repeat-dose toxicity</li>
                                    <li>Reproduction and developmental toxicity</li>
                                    <li>Genotoxicity</li>
                                    <li>Carcinogenicity</li>
                                    <li>Review of clinical adverse effects and evidence</li>
                                    <li>
                                        Use of Threshold of Toxicological Concern (TTC) approach for
                                        genotoxic substances [ICH-M7(R2)]
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </article>

                    {/* RIGHT CARD — simple li only */}
                    <article className="tox-card">
                        <ul className="tox-cardList tox-cardList--secondary">
                            <li>
                                PDE calculation in compliance with EMA/CHMP/CVMP/SWP/169430/2012
                            </li>
                            <li>
                                PDE reports approved by board-certified (DABT/ERT) and
                                experienced toxicologists
                            </li>
                            <li>
                                Identification of critical effects, Points of Departure (POD),
                                NOAEL/NOEL, and LOAEL from the most appropriate
                                nonclinical/clinical studies
                            </li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* ====== SECTION 2: Regulatory Toxicology & ERA Support ====== */}
            <section className="tox-section tox-cardSection">
                <h2>Regulatory Toxicology &amp; ERA Support</h2>

                <div className="tox-cardRow">
                    {/* LEFT CARD — li with nested ul */}
                    <article className="tox-card">
                        <ul className="tox-cardList">
                            <li>
                                Environmental Risk Assessment
                                <ul>
                                    <li>Phase 1 ERA reporting</li>
                                    <li>
                                        Phase 2a/2b ERA reporting in alliance with GLP-certified
                                        preclinical laboratories
                                    </li>
                                </ul>
                            </li>

                            <li>
                                Computational Toxicology
                                <ul>
                                    <li>Toxicological Risk Assessment</li>
                                    <li>Forced Degradation Analysis</li>
                                    <li>Metabolite Profiling</li>
                                    <li>Read-Across based predictions</li>
                                    <li>Mutagenicity predictions</li>
                                </ul>
                            </li>
                        </ul>
                    </article>

                    {/* RIGHT CARD — simple li only */}
                    <article className="tox-card">
                        <ul className="tox-cardList tox-cardList--secondary">
                            <li>
                                Derivation of PDE/ADE values for uncommon or other routes of
                                exposure (e.g., nasal, ocular, otic, topical/dermal, including
                                intravenous infusions)
                            </li>
                            <li>
                                Establishment of PDE levels for cleaning agents, starting
                                materials, solvents, intermediates, and complex mixtures
                            </li>
                            <li>
                                GMP audit support (post/during), including answering Agency
                                queries
                            </li>
                            <li>Technical support during cleaning validation</li>
                            <li>
                                Non-clinical Overview and Summaries: preparation of Non-clinical
                                Overview (Module 2.4) and Non-clinical Summary (Module 2.6),
                                plus Module 4 for eCTD submissions (IND, CTA, NDA, BLA, MAA)
                            </li>
                            <li>
                                Non-clinical sections of meeting packages: pre-IND/CTA and
                                pre-NDA/BLA/MAA sponsor packages
                            </li>
                            <li>
                                Non-clinical sections of Investigator’s Brochure (IB): authoring
                                new IBs and ongoing maintenance / updates through product
                                approval
                            </li>
                            <li>Weight-of-Evidence (WoE) assessments for carcinogenicity</li>
                            <li>Scientific and regulatory review of non-clinical documents</li>
                            <li>
                                Development and review of non-clinical study plans/protocols
                            </li>
                            <li>Non-clinical development strategy for regulatory submissions</li>
                            <li>GLP audits for test facilities</li>
                            <li>
                                CRO identification and qualification for non-clinical regulatory
                                services
                            </li>
                        </ul>
                    </article>
                </div>
            </section>

            {/* ====== SECTION 3: Why Eupraxia card (single tile) ====== */}
            <section className="tox-section tox-whySection">
                <article className="tox-whyCard">
                    <h3>Why Eupraxia for Toxicological Assessment?</h3>
                    <p className="tox-whyIntro">
                        Eupraxia Consulting is a US &amp; India based CRO delivering data
                        solutions for regulatory submissions that are cost-advantageous,
                        faster, accurate, and hassle-free, using advances in data
                        technologies and traditional wisdom. We support non-clinical drug
                        discovery, clinical development, pharma manufacturing/CMC regulatory
                        compliance, as well as medical devices/IVD, biotech, and vaccines.
                    </p>
                    <ul className="tox-cardList">
                        <li>
                            Specialized consulting firm focused on non-clinical regulatory
                            data services
                        </li>
                        <li>
                            Industry leader in SEND (Standard for Exchange of Nonclinical
                            Data) dataset creation
                        </li>
                        <li>Dedicated to regulatory excellence, quality, and timely delivery</li>
                    </ul>
                </article>
            </section>
        </div>
    );
}
